<template>
    <nav :class="{ 'menu--open': isOpen }" class="menu" role="navigation">
        <ul class="menu__container">
            <slot></slot>
        </ul>
    </nav>
</template>

<script>
    import EventBus, { events as EventBusEvents } from '@/event-bus';
    import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

    const menuItemOpenClassName = 'menu__item--dropdown-open';

    export default {
        name: 'LayoutMenu',

        props: {
            isOpen: { type: Boolean, default: false },
        },

        data () {
            return {
                menuItemsHavingChildren: null,
            };
        },

        watch: {
            isOpen (newValue) {
                this.setIsBodyElementScrollingPrevented(newValue);
            },
        },

        mounted () {
            this.menuItemsHavingChildren = this.$el.querySelectorAll('.menu__item--has-children');

            // This is not the VueJS way but as the element we need to listen to is in <slot>ed content, can't easily use v-on directives.
            this.attachMenuEvents();

            EventBus.$on(EventBusEvents.MENU_DROPDOWN_CLOSE, this.closeAllDropdowns);
        },

        destroyed () {
            // When attaching plain JS events, they need to be unbound when no longer in use to prevent memory leaks
            this.detachEvents();
            EventBus.$off(EventBusEvents.MENU_DROPDOWN_CLOSE, this.closeAllDropdowns);
        },

        methods: {
            attachMenuEvents () {
                for (const menuItem of this.menuItemsHavingChildren) {
                    menuItem.addEventListener('mouseenter', this.onMenuItemHavingChildrenClicked);
                    menuItem.addEventListener('mouseleave', this.onMenuItemHavingChildrenClicked);
                    menuItem.addEventListener('click', this.onMenuItemHavingChildrenClicked);
                }
            },

            detachEvents () {
                for (const menuItem of this.menuItemsHavingChildren) {
                    menuItem.removeEventListener('mouseenter', this.onMenuItemHavingChildrenClicked);
                    menuItem.removeEventListener('mouseleave', this.onMenuItemHavingChildrenClicked);
                    menuItem.removeEventListener('click', this.onMenuItemHavingChildrenClicked);
                }
            },

            /**
             * Filter events based on viewport width:
             *   * on large screens, cancel mouseenter & mouseleave, but allow clicks
             *   * on small screens, cancel click but allow mouseenter & mouseleave
             * @param event
             * @return {boolean}
             */
            eventShouldBeCancelled (event) {
                const isDesktop = window.matchMedia('(min-width: 1200px)').matches;

                switch (event.type.toLowerCase()) {
                    case 'mouseenter':
                    case 'mouseleave':
                        if (!isDesktop) {
                            return true;
                        }
                        break;
                    case 'click':
                        if (isDesktop) {
                            return true;
                        }
                        break;
                }

                return false;
            },

            onMenuItemHavingChildrenClicked (event) {
                if (this.eventShouldBeCancelled(event)) {
                    return;
                }

                const classList = event.currentTarget.classList;
                const wasOpen = classList.contains(menuItemOpenClassName);

                classList.toggle(menuItemOpenClassName);
                if (
                    // @formatter:off
                    wasOpen &&
                    (event.currentTarget === event.target || event.currentTarget === event.target.parentElement)
                // @formatter:on
                ) {
                    // Make sure accessibility-related rules (focus-within) do not prevent the menu from closing when
                    // the menu item having children is clicked again
                    document.activeElement.blur();
                    event.preventDefault();
                    event.stopPropagation();
                }

                EventBus.$emit(wasOpen ? EventBusEvents.MENU_DROPDOWN_CLOSE : EventBusEvents.MENU_DROPDOWN_OPEN);
            },

            closeAllDropdowns () {
                for (const element of document.getElementsByClassName(menuItemOpenClassName)) {
                    element.classList.remove(menuItemOpenClassName);
                }
            },

            setIsBodyElementScrollingPrevented (isPrevented) {
                isPrevented ? disableBodyScroll(this.$el) : enableBodyScroll(this.$el);
            },
        },
    };
</script>

<style scoped lang="scss">
    @import "~@/assets/styles/abstracts/_placeholders";
    @import "~@/assets/styles/abstracts/_mixins";

    $dropdownPadding: 3rem;

    .menu {
        background: $white;

        transform-origin: left;
        transform: translate(-100%);
        opacity: 0;
        transition: all 250ms ease-in-out;
        visibility: hidden;

        @include media-breakpoint-up (lg) {
            transform: none;
            opacity: 1;
            visibility: visible;
        }

        &#{&}--open {
            transform: none;
            opacity: 1;
            visibility: visible;
        }

        &__container {
            display: flex;
            flex-direction: column;

            margin: 0;
            padding: 0;

            list-style: none;

            @media screen and (min-width: map_get($grid-breakpoints, 'lg')) {
                flex-direction: row;
            }
        }

        &__item {
            border-top: 0.1rem solid $gray-300;

            color: #242b49;
            font-size: 2.5rem;

            cursor: pointer;

            transition: background-color 100ms ease-in-out;

            @include media-breakpoint-up(lg) {
                border: none;

                font-size: 1.6rem;
            }

            &--has-children {
                position: relative;

                @include media-breakpoint-up(lg) {
                    padding-right: 1.5em;
                }

                & > .menu__item-inner {
                    position: relative;

                    &::after {
                        content: '';
                        @include place(50%, 1em);
                        @include size(1em, 1em);

                        transform: translateY(-50%);

                        background: url('~@/assets/images/chevron.svg') no-repeat center;

                        transition: transform 200ms ease-in-out;
                        transform-origin: center center;

                        @include media-breakpoint-up(lg) {
                            right: -0.5em;
                        }
                    }
                }
            }

            &--dropdown-open,
            &:focus-within,
            &:focus,
            &:hover {
                background-color: $gray-100;
                color: $color-brand;

                @include media-breakpoint-up(lg) {
                    color: $body-color;
                }
            }

            @mixin menu-open {
                .menu__item-inner::after {
                    transform: translateY(-50%) rotateX(180deg);
                }

                .menu__dropdown {
                    max-height: 30rem;

                    visibility: visible;
                    transform: none;
                    opacity: 1;
                }
            }

            &--dropdown-open,
            &:focus,
            &:focus-within {
                @include menu-open;
            }

            &:hover {
                @include media-breakpoint-up(lg) {
                    @include menu-open;
                }
            }

            &--dropdown-open {
                // Masque l'outline (bleu ciel flou dans Chrome) des éléments ayant le focus.
                // Uniquement si le menu a été ouvert par un clic. Ça permet de préserver l'accessibilité pour ceux qui
                // naviguent au clavier sans que les peintres et les CPs le sachent parce qu'ils trouverait ça moche !
                outline: none;
            }
        }

        &__item-inner {
            display: block;

            padding: 2rem;

            color: inherit;
            text-decoration: none;

            @include media-breakpoint-up(lg) {
               padding: 2.8rem;
            }
        }

        &__dropdown {
            overflow: hidden;
            visibility: hidden;

            max-height: 0;

            margin: 0;
            padding: 0;

            list-style: none;
            background-color: $white;

            opacity: 0;
            transform: translateY(-2rem);

            transition: all 250ms ease-in-out;

            @include media-breakpoint-up(lg) {
                overflow: visible;
                position: absolute;
                left: 0;

                min-width: 27rem;
                max-height: none;

                padding: .6rem 0;
                border: 0.1rem solid $gray-200;

                transition-duration: 100ms;
            }
        }
    }

    .dropdown {
        &__item {
            display: flex;
            flex-direction: column;
            position: relative;

            padding: 0;

            text-transform: none;
        }

        @include media-breakpoint-up(lg) {
            &__item + &__item::before {
                content: "";

                margin: 0 $dropdownPadding;
                border-top: 0.1rem solid $gray-200;
            }
        }

        &__item-link {
            padding: 1rem 2rem;

            font-size: 1.8rem;

            @include media-breakpoint-up(lg) {
                padding: 1.6rem $dropdownPadding;

                font-size: 1.4rem;
            }
        }
    }
</style>
